import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { id: "how-it-works", label: "How It Works", testid: "nav-link-how-it-works" },
  { id: "indicator", label: "Indicator", testid: "nav-link-indicator" },
  { id: "results", label: "Results", testid: "nav-link-results" },
  { id: "community", label: "Community", testid: "nav-link-community" },
  { id: "exclusive", label: "Exclusive", testid: "nav-link-exclusive" },
  { id: "founder", label: "Founder", testid: "nav-link-founder" },
  { id: "faq", label: "FAQ", testid: "nav-link-faq" },
];

const Logo = () => (
  <span className="flex items-center gap-2.5" data-testid="nav-logo">
    <img src="/atlas-logo-256.png" alt="Atlas logo" className="w-9 h-9 rounded-full ring-1 ring-gold/30 object-cover" />
    <span className="font-display tracking-[0.3em] text-lg text-slate-100 font-bold">ATLAS</span>
  </span>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToSection(id);
    }
  };

  const goGiveaway = () => {
    setOpen(false);
    if (location.pathname !== "/giveaway") {
      navigate("/giveaway");
    }
    window.scrollTo(0, 0);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || location.pathname !== "/" ? "glass-panel border-line/80" : "bg-transparent border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => go("top")} className="focus:outline-none" aria-label="Atlas home">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-link-giveaway"
            onClick={goGiveaway}
            className={`font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
              location.pathname === "/giveaway" ? "text-gold" : "text-slate-400 hover:text-gold"
            }`}
          >
            Giveaway
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-join-button"
            onClick={() => go("join")}
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-gold-bright transition-colors duration-300 tracking-wide"
          >
            Join Atlas
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden text-slate-300 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-panel border-t border-line/80 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-${l.testid}`}
              onClick={() => go(l.id)}
              className="text-left font-mono text-xs uppercase tracking-[0.25em] text-slate-300 hover:text-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-nav-link-giveaway"
            onClick={goGiveaway}
            className={`text-left font-mono text-xs uppercase tracking-[0.25em] transition-colors ${
              location.pathname === "/giveaway" ? "text-gold" : "text-slate-300 hover:text-gold"
            }`}
          >
            Giveaway
          </button>
          <button
            data-testid="mobile-nav-join-button"
            onClick={() => go("join")}
            className="mt-2 bg-gold text-ink font-semibold text-sm px-5 py-3 rounded-sm text-center"
          >
            Join Atlas
          </button>
        </div>
      )}
    </header>
  );
};

export default Nav;
